/**
 * SERVIÇO CENTRALIZADO DE VISIBILIDADE, APROVAÇÃO E ATIVAÇÃO OPERACIONAL DE ARENAS E QUADRAS
 * =========================================================================================
 * Módulo autoritativo para o BLOCO 10 (ATLETAHUB).
 *
 * Distingue com isolamento rigoroso os três conceitos fundamentais:
 * A) APROVAÇÃO DOCUMENTAL (isArenaApproved)
 * B) ATIVAÇÃO OPERACIONAL PELO ADMIN (isArenaOperationallyActive)
 * C) VISIBILIDADE PARA ATLETAS NA DESCOBERTA PÚBLICA (isArenaPubliclyDiscoverable)
 *
 * Regra Geográfica Mandatória do Bloco 10:
 * A localização de referência para a descoberta geográfica da Arena é a cidade/estado do
 * PROPRIETÁRIO da Arena (users/{ownerUid}), e NÃO o endereço físico de uma quadra individual.
 */

import { CourtItem, UserProfile } from "../../types/userProfile";

/**
 * Normalização de strings para comparação resiliente a acentuação e caixa
 */
export const normalizeDiscoveryText = (str: string | undefined | null): string =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

/**
 * 1. APROVAÇÃO DOCUMENTAL (isArenaApproved)
 * Retorna true se e somente se a Arena possui status formal de aprovação documental.
 * Status canônicos aprovados: "VERIFIED", "VERIFICADA", "APPROVED", isVerifiedArena === true.
 * NUNCA aprovada se PENDING_APPROVAL, PENDING, UNDER_REVIEW, REJECTED, BLOCKED, etc.
 */
export function isArenaApproved(arenaOrCourt: any): boolean {
  if (!arenaOrCourt) return false;

  const vStatus = (
    arenaOrCourt.verificationStatus ||
    arenaOrCourt.resolvedVerificationStatus ||
    arenaOrCourt.arenaVerificationStatus ||
    ""
  ).toString().toUpperCase().trim();

  // Status de pendência, análise, bloqueio, suspensão ou rejeição NUNCA são aprovados
  const isPendingOrBlocked = [
    "PENDING_APPROVAL",
    "PENDING",
    "PENDENTE",
    "UNDER_REVIEW",
    "EM_ANALISE",
    "EM_ANÁLISE",
    "REJECTED",
    "REJEITADA",
    "BLOCKED",
    "BLOQUEADA",
    "SUSPENDED",
    "SUSPENSA",
  ].includes(vStatus);

  if (isPendingOrBlocked) return false;

  const isVerifiedString = ["VERIFIED", "VERIFICADA", "APPROVED", "APROVADA"].includes(vStatus);
  const isVerifiedFlag = arenaOrCourt.isVerifiedArena === true || arenaOrCourt.isVerified === true;

  // PONTO 1: isArenaApproved() depende estritamente da verificação/aprovação.
  // Ativação operacional (status "ATIVA", "ACTIVE", "EM_DIA") NÃO significa aprovação formal.
  return isVerifiedString || isVerifiedFlag;
}

/**
 * 2. ATIVAÇÃO OPERACIONAL PELO ADMIN (isArenaOperationallyActive)
 * O Super Admin tem o poder de ATIVAR ou DESATIVAR a Arena operacionalmente.
 * Retorna true se o status operacional NÃO for INATIVA, BLOQUEADA ou SUSPENSA, e isActive !== false.
 */
export function isArenaOperationallyActive(arenaOrCourt: any): boolean {
  if (!arenaOrCourt) return false;

  const status = (arenaOrCourt.status || "").toString().toUpperCase().trim();
  const isActive = arenaOrCourt.isActive;

  if (isActive === false) return false;
  if (status === "INATIVA" || status === "INACTIVE") return false;
  if (status === "BLOQUEADA" || status === "BLOCKED") return false;
  if (status === "SUSPENSA" || status === "SUSPENDED") return false;

  if (status === "ATIVA" || status === "ACTIVE") return true;

  return isActive !== false;
}

/**
 * 3. ATIVAÇÃO OPERACIONAL DE QUADRA (isCourtOperationallyActive)
 * Quadra individual deve estar operacionalmente ativa para receber novos agendamentos.
 */
export function isCourtOperationallyActive(court: any): boolean {
  if (!court) return false;

  const status = (court.status || "").toString().toLowerCase().trim();
  const isActive = court.isActive;
  const active = court.active;

  if (isActive === false || active === false) return false;
  if (status === "inactive" || status === "inativa" || status === "inactive_commercial_limit") return false;
  if (status === "blocked" || status === "bloqueada") return false;
  if (status === "suspended" || status === "suspensa") return false;

  return true;
}

/**
 * 3.1. APROVAÇÃO FORMAL DA QUADRA (isCourtApproved)
 * BLOCO 7:
 * Quadras em PENDING_APPROVAL, PENDING, UNDER_REVIEW, REJECTED, BLOCKED ou SUSPENDED NÃO são aprovadas.
 * Quadras com status "VERIFIED", "VERIFICADA", "APPROVED", "APROVADA" ou isVerifiedArena === true são aprovadas.
 */
export function isCourtApproved(court: any): boolean {
  if (!court) return false;

  const vStatus = (court.verificationStatus || "").toString().toUpperCase().trim();
  const status = (court.status || "").toString().toLowerCase().trim();

  // Status de reprovação ou pendência estrita
  if (
    vStatus === "PENDING_APPROVAL" ||
    vStatus === "PENDING" ||
    vStatus === "UNDER_REVIEW" ||
    vStatus === "EM_ANALISE" ||
    vStatus === "EM_ANÁLISE" ||
    vStatus === "REJECTED" ||
    vStatus === "REJEITADA" ||
    vStatus === "BLOCKED" ||
    vStatus === "BLOQUEADA" ||
    vStatus === "SUSPENDED" ||
    vStatus === "SUSPENSA" ||
    status === "pending" ||
    status === "pendente" ||
    status === "rejected" ||
    status === "rejeitada" ||
    status === "blocked" ||
    status === "bloqueada" ||
    status === "suspended" ||
    status === "suspensa"
  ) {
    return false;
  }

  const isApprovedString = ["APPROVED", "APROVADA", "VERIFIED", "VERIFICADA"].includes(vStatus);
  const isVerifiedFlag = court.isVerifiedArena === true || court.isVerified === true;

  // PONTO 2: Uma quadra precisa possuir evidência explícita de aprovação.
  // status = active + verificationStatus ausente NÃO deve ser automaticamente interpretado como aprovação formal.
  return isApprovedString || isVerifiedFlag;
}

/**
 * 4. AGENDAMENTO PÚBLICO DE QUADRA (isCourtPubliclyBookable)
 * BLOCO 7 & BLOCO 10:
 * Uma quadra só pode ser agendada publicamente por atletas se:
 * - A quadra estiver formalmente aprovada (isCourtApproved)
 * - A quadra estiver operacionalmente ativa (isCourtOperationallyActive)
 * - A arena estiver aprovada (isArenaApproved)
 * - A arena estiver operacionalmente ativa (isArenaOperationallyActive)
 */
export function isCourtPubliclyBookable(court: CourtItem, arenaContext?: any): boolean {
  if (!court) return false;
  if (!isCourtApproved(court)) return false;
  if (!isCourtOperationallyActive(court)) return false;

  const context = arenaContext || court;
  if (!isArenaApproved(context)) return false;
  if (!isArenaOperationallyActive(context)) return false;

  return true;
}

/**
 * 5. LOCALIZAÇÃO DO PROPRIETÁRIO (resolveArenaOwnerLocation)
 * REGRA CANÔNICA DO BLOCO 10:
 * A cidade/estado do PROPRIETÁRIO da Arena (users/{ownerUid}) é a referência mandatória para a
 * descoberta geográfica da Arena.
 * Se o proprietário alterar seu endereço de Cidade A para Cidade B, a Arena passa a ser
 * encontrada em Cidade B e deixa de aparecer em Cidade A.
 */
export function resolveArenaOwnerLocation(
  arena: any,
  ownerProfile?: UserProfile | null,
  allOwners?: UserProfile[] | Map<string, UserProfile> | Record<string, UserProfile>
): { city: string; state: string; source: "owner" | "arena" | "court" | "none" } {
  const arenaOwnerUid = (
    arena.ownerUid ||
    arena.ownerId ||
    arena.userId ||
    arena.arenaOwnerId ||
    ""
  ).trim();
  const arenaId = (arena.id || arena.arenaId || "").trim();

  // 1. Tenta ownerProfile direto
  if (ownerProfile) {
    const ownerUid = (ownerProfile.uid || ownerProfile.id || "").trim();
    const isMatch =
      (ownerUid && arenaOwnerUid && ownerUid === arenaOwnerUid) ||
      (ownerProfile.arenaId && arenaId && ownerProfile.arenaId === arenaId) ||
      (!arenaOwnerUid && !arenaId) ||
      (ownerUid && !arenaOwnerUid);

    if (isMatch && (ownerProfile.city || ownerProfile.state)) {
      return {
        city: (ownerProfile.city || "").trim(),
        state: (ownerProfile.state || "").trim(),
        source: "owner",
      };
    }
  }

  // 2. Tenta encontrar no catálogo de usuários/proprietários (allOwners)
  if (allOwners) {
    let matched: UserProfile | undefined;

    if (allOwners instanceof Map) {
      if (arenaOwnerUid && allOwners.has(arenaOwnerUid)) {
        matched = allOwners.get(arenaOwnerUid);
      } else if (arenaId && allOwners.has(arenaId)) {
        matched = allOwners.get(arenaId);
      } else {
        for (const u of allOwners.values()) {
          const uUid = (u.uid || u.id || "").trim();
          if (
            (arenaOwnerUid && uUid === arenaOwnerUid) ||
            (arenaId && u.arenaId && u.arenaId.trim() === arenaId)
          ) {
            matched = u;
            break;
          }
        }
      }
    } else if (Array.isArray(allOwners)) {
      matched = allOwners.find((u) => {
        const uUid = (u.uid || u.id || "").trim();
        return (
          (arenaOwnerUid && uUid === arenaOwnerUid) ||
          (arenaId && u.arenaId && u.arenaId.trim() === arenaId)
        );
      });
    } else if (typeof allOwners === "object") {
      matched = (allOwners as any)[arenaOwnerUid] || (allOwners as any)[arenaId];
    }

    if (matched && (matched.city || matched.state)) {
      return {
        city: (matched.city || "").trim(),
        state: (matched.state || "").trim(),
        source: "owner",
      };
    }
  }

  // 3. Fallback se não houver perfil de proprietário: dados da arena
  if (arena.city || arena.state) {
    return {
      city: (arena.city || "").trim(),
      state: (arena.state || "").trim(),
      source: "arena",
    };
  }

  // 4. Fallback da quadra
  if (arena.courts && Array.isArray(arena.courts) && arena.courts.length > 0) {
    const first = arena.courts[0];
    return {
      city: (first.city || "").trim(),
      state: (first.state || "").trim(),
      source: "court",
    };
  }

  return { city: "", state: "", source: "none" };
}

/**
 * 6. ELEGIBILIDADE PARA DESCOBERTA PÚBLICA (isArenaPubliclyDiscoverable)
 * Uma Arena SÓ DEVE APARECER para os atletas se:
 * 1. isArenaApproved === true (VERIFIED/APROVADA);
 * 2. isArenaOperationallyActive === true (ATIVA / isActive !== false);
 * 3. Se a busca for geográfica:
 *    Localização do PROPRIETÁRIO (users/{ownerUid}) coincide com os filtros do atleta.
 * Se a busca for "Procurar todos" (isSearchAll ou campos de busca vazios):
 *    Ignora a cidade/estado, mas CONTINUA EXIGINDO os critérios 1 e 2.
 */
export function isArenaPubliclyDiscoverable(
  paramsOrArena: any,
  options?: {
    ownerProfile?: UserProfile | null;
    allOwners?: UserProfile[] | Map<string, UserProfile> | Record<string, UserProfile>;
    searchCity?: string;
    searchState?: string;
    isSearchAll?: boolean;
  }
): boolean {
  let arena: any;
  let ownerProfile: UserProfile | null | undefined;
  let allOwners: any;
  let searchCity = "";
  let searchState = "";
  let isSearchAll = false;

  if (paramsOrArena && typeof paramsOrArena === "object" && "arena" in paramsOrArena) {
    arena = paramsOrArena.arena;
    ownerProfile = paramsOrArena.ownerProfile;
    allOwners = paramsOrArena.allOwners;
    searchCity = paramsOrArena.searchCity || "";
    searchState = paramsOrArena.searchState || "";
    isSearchAll = Boolean(paramsOrArena.isSearchAll);
  } else {
    arena = paramsOrArena;
    if (options) {
      ownerProfile = options.ownerProfile;
      allOwners = options.allOwners;
      searchCity = options.searchCity || "";
      searchState = options.searchState || "";
      isSearchAll = Boolean(options.isSearchAll);
    }
  }

  if (!arena) return false;

  // 1. Aprovação Documental
  if (!isArenaApproved(arena)) return false;

  // 2. Ativação Operacional
  if (!isArenaOperationallyActive(arena)) return false;

  // 2.1. Pelo menos uma quadra aprovada e ativa (se a lista de quadras for fornecida no objeto da arena)
  const courtsList = arena.courts || arena.allCourts;
  if (Array.isArray(courtsList)) {
    const hasActiveApprovedCourt = courtsList.some((c: any) => isCourtApproved(c) && isCourtOperationallyActive(c));
    if (!hasActiveApprovedCourt) return false;
  }

  // 3. Critério Geográfico
  const cleanCity = (searchCity || "").trim();
  const cleanState = (searchState || "").trim();
  const effectiveSearchAll = isSearchAll || (!cleanCity && !cleanState);

  if (effectiveSearchAll) {
    return true;
  }

  // Resolve a localização referenciada no PROPRIETÁRIO
  const ownerLoc = resolveArenaOwnerLocation(arena, ownerProfile, allOwners);
  const normOwnerCity = normalizeDiscoveryText(ownerLoc.city);
  const normOwnerState = normalizeDiscoveryText(ownerLoc.state);

  const normSearchCity = normalizeDiscoveryText(cleanCity);
  const normSearchState = normalizeDiscoveryText(cleanState);

  if (normSearchCity) {
    const cityMatches =
      normOwnerCity.includes(normSearchCity) || normSearchCity.includes(normOwnerCity);
    if (!cityMatches) return false;
  }

  if (normSearchState) {
    const stateMatches = normOwnerState === normSearchState;
    if (!stateMatches) return false;
  }

  return true;
}
